import type {
  ConversationId,
  ConversationThreadId,
  MessageId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { DraftResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { memo } from "react";
import { useMessageInputState } from "../hooks/use-message-input-state";
import type { ReplyMode } from "../hooks/use-reply-mode";
import { useReplyMode } from "../hooks/use-reply-mode";
import { ChatToolbar } from "./message-editor/chat-toolbar";
import { EmailToolbar } from "./message-editor/email-toolbar";
import { MessageEditor } from "./message-editor/message-editor";
import { MessageInputActions } from "./message-input-actions";
import { ReplyModeDropdown } from "./reply-mode-dropdown";

interface EmailMessageInputProps {
  mode: "email";
  channel: MessageChannel;
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  inReplyToMessageId: MessageId;
  initialReplyMode?: ReplyMode;
  initialDraft?: DraftResponse | null;
  onDiscardDraft?: () => void;
}

interface ChatMessageInputProps {
  mode: "chat";
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  inReplyToMessageId?: MessageId;
}

type MessageInputProps = EmailMessageInputProps | ChatMessageInputProps;

export const MessageInput = memo(function MessageInput(
  props: MessageInputProps,
) {
  const { mode, conversationId, threadId } = props;

  const replyModeState = useReplyMode(
    mode === "email" ? (props.initialReplyMode ?? "reply") : "reply",
  );

  const state = useMessageInputState({
    conversationId,
    threadId,
    editorMode: mode,
    channel: mode === "email" ? props.channel : undefined,
    inReplyToMessageId: props.inReplyToMessageId,
    initialDraft: mode === "email" ? props.initialDraft : undefined,
    onDiscardDraft: mode === "email" ? props.onDiscardDraft : undefined,
  });

  const actions = (
    <MessageInputActions
      deleteDraftAction={state.deleteDraftAction}
      isDeletingDraft={state.isDeletingDraft}
      isSavingDraft={state.isSavingDraft}
      isSending={state.isSending}
      onDeleteDraft={state.handleDeleteDraft}
      onSend={state.handleSend}
      onSendAndClose={state.handleSendAndClose}
      onSendAndMarkInProgress={state.handleSendAndMarkInProgress}
      sendAction={state.sendAction}
    />
  );

  const toolbar =
    mode === "email" ? (
      <EmailToolbar
        isGeneratingAiResponse={state.isGeneratingAiResponse}
        onFileSelect={state.handleFileSelect}
        onGenerateAiResponse={state.handleGenerateAiResponse}
        onImageSelect={state.handleImageSelect}
      />
    ) : (
      <ChatToolbar
        onFileSelect={state.handleFileSelect}
        onImageSelect={state.handleImageSelect}
      />
    );

  return (
    <div
      className={`flex w-full flex-col${mode === "email" ? " py-4" : ""}`}
      ref={state.containerRef}
    >
      <MessageEditor
        actions={actions}
        attachments={state.attachments}
        className="w-full"
        editorState={state.editorState}
        header={
          mode === "email" ? (
            <ReplyModeDropdown replyModeState={replyModeState} />
          ) : undefined
        }
        onChange={state.handleEditorChange}
        onRemoveAttachment={state.handleRemoveAttachment}
        placeholder={
          mode === "email" ? "Type your message..." : "Start typing..."
        }
        toolbar={toolbar}
      />
    </div>
  );
});
